import { Component, Input, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { SuiButtonComponent } from '@sui/button';
import { SuiEmptyStateComponent } from '@sui/empty-state';

import {
  AlertNotificationsScope,
  injectLatestAlertNotificationsQuery,
  injectMarkAllAlertNotificationsReadMutation,
} from 'src/app/api/queries/alert-notification.queries';
import { AlertNotification } from 'src/app/core/models/alert-notification.models';
import { selectUser } from 'src/app/core/state';
import { AppState } from 'src/app/core/state/app.state';
import { ALERT_NOTIFICATIONS_ROUTE } from 'src/app/alerts/alert-notification.utils';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { LoadingComponent } from '../../../../shared/components/loading/loading.component';

/**
 * The bell dropdown (WAP-1860): the latest rows of the user's alert
 * notification stream, unread while `time_read` is null. A row opens the
 * event in the stream page, which is what marks it read (D-1); the footer
 * opens the stream and marks every row read.
 */
@Component({
  selector: 'app-dropdown-alerts',
  templateUrl: './dropdown-alerts.component.html',
  styleUrls: ['./dropdown-alerts.component.scss'],
  imports: [
    SuiEmptyStateComponent,
    DropdownComponent,
    LoadingComponent,
    SuiButtonComponent,
    TranslatePipe,
  ],
})
export class DropdownAlertsComponent {
  @Input() closeMethod!: () => void;

  private readonly store = inject(Store<AppState>);
  private readonly router = inject(Router);

  private readonly user = this.store.selectSignal(selectUser);

  private readonly scope = computed<AlertNotificationsScope | null>(() => {
    const user = this.user();

    return user?.id
      ? { userId: user.id, companyId: user.active_company_id }
      : null;
  });

  private readonly latestQuery = injectLatestAlertNotificationsQuery(
    this.scope,
  );
  readonly markAllRead = injectMarkAllAlertNotificationsReadMutation();

  readonly rows = computed(() => this.latestQuery.data() ?? []);
  readonly rowsLoading = computed(() => this.latestQuery.isPending());
  readonly unreadCount = computed(
    () => this.rows().filter((row) => row.time_read === null).length,
  );

  openEvent(row: AlertNotification): void {
    this.closeMethod?.();
    this.router.navigate([ALERT_NOTIFICATIONS_ROUTE, row.id]);
  }

  openStream(): void {
    this.closeMethod?.();
    this.router.navigate([ALERT_NOTIFICATIONS_ROUTE]);
  }

  markAllAsRead(): void {
    this.markAllRead.mutate(undefined);
  }
}
